import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const Home = () => {
  const [activeLocation, setActiveLocation] = useState(null);

  const scrollToLocation = (location) => {
    setActiveLocation(location);
    const element = document.getElementById(`location-${location}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-turquoise-50 to-white">
      <SEO 
        title="Ambulante Pflege Großstadtherzen® - Startseite"
        description="Professionelle ambulante Pflege mit Herz in Leipzig und Halle (Saale). Grundpflege, Behandlungspflege, Hauswirtschaft und individuelle Beratung."
        keywords="ambulante Pflege, Pflegedienst Leipzig, Pflegedienst Halle, häusliche Pflege, Großstadtherzen"
        canonicalPath="/"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-r from-turquoise-400 to-turquoise-500 opacity-10"></div>
        <div className="container mx-auto text-center relative z-10">
          <div className="mb-8">
            <img 
              src="https://beratung.grossstadtherzen.de/logo-transparent.png" 
              alt="Großstadtherzen Logo" 
              className="h-96 w-auto mx-auto mb-6"
              data-testid="hero-logo"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Ambulante Pflege <span className="text-turquoise-400">Großstadtherzen®</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Bei uns werden sie „Groß" geschrieben
          </p>
          <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
            Professionelle ambulante Pflege mit Herz in Leipzig und Halle (Saale)
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/leipzig"
              className="bg-turquoise-400 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-turquoise-500 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              data-testid="hero-cta-leipzig"
            >
              Leipzig
            </Link>
            <Link 
              to="/halle"
              className="bg-turquoise-400 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-turquoise-500 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              data-testid="hero-cta-halle"
            >
              Halle (Saale)
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-white" data-testid="about-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Warum Großstadtherzen?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir bieten professionelle ambulante Pflege mit persönlicher Betreuung und großem Herzen
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-turquoise-50 to-turquoise-100 hover:shadow-xl transition-shadow" data-testid="feature-card-1">
              <div className="bg-turquoise-400 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                ✓
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Professionelle Pflege</h3>
              <p className="text-gray-600">
                Unser qualifiziertes Team bietet hochwertige ambulante Pflegeleistungen mit langjähriger Erfahrung
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-turquoise-50 to-turquoise-100 hover:shadow-xl transition-shadow" data-testid="feature-card-2">
              <div className="bg-turquoise-400 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                ♥
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Mit Herz & Seele</h3>
              <p className="text-gray-600">
                Bei uns stehen Sie im Mittelpunkt. Wir pflegen mit Empathie, Respekt und echter Menschlichkeit
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-turquoise-50 to-turquoise-100 hover:shadow-xl transition-shadow" data-testid="feature-card-3">
              <div className="bg-turquoise-400 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                ⌚
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Verlässlich & Nah</h3>
              <p className="text-gray-600">
                Zwei Standorte in Leipzig und Halle ermöglichen uns, schnell und zuverlässig für Sie da zu sein
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section id="locations" className="py-20 px-4 bg-gray-50" data-testid="locations-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Unsere Standorte</h2>
            <p className="text-xl text-gray-600">Wählen Sie Ihren Standort</p>
          </div>

          {/* Leipzig */}
          <div id="location-leipzig" className="mb-16 scroll-mt-20">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden hover:shadow-3xl transition-shadow">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto">
                  <img 
                    src="https://images.unsplash.com/photo-1765896387398-1e1ae8d2eb85?q=85&w=800" 
                    alt="Pflege in Leipzig" 
                    className="w-full h-full object-cover"
                    data-testid="leipzig-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-turquoise-400/20 to-transparent"></div>
                </div>
                <div className="p-8 md:p-12">
                  <h3 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                    <span className="bg-turquoise-400 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4">L</span>
                    Leipzig
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Adresse
                      </h4>
                      <p className="text-gray-600 ml-7">
                        Ambulante Pflege Großstadtherzen® UG<br />
                        Zschochersche Str. 49<br />
                        04229 Leipzig
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Kontakt
                      </h4>
                      <p className="text-gray-600 ml-7">
                        <a href="tel:034124927833" className="hover:text-turquoise-400 transition-colors">Telefon: 0341-24927833</a><br />
                        <span className="text-gray-500">Fax: 0341-24927832</span><br />
                        <a href="mailto:info@grossstadtherzen.de" className="hover:text-turquoise-400 transition-colors">info@grossstadtherzen.de</a>
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Bürozeiten
                      </h4>
                      <div className="text-gray-600 ml-7 space-y-1">
                        <p>Montag - Freitag: 8:00 - 16:00 Uhr</p>
                        <p>Samstag: nach Vereinbarung</p>
                      </div>
                    </div>
                    
                    <Link 
                      to="/leipzig"
                      className="inline-block bg-turquoise-400 text-white px-6 py-3 rounded-full font-semibold hover:bg-turquoise-500 transition-colors mt-4"
                      data-testid="leipzig-details-link"
                    >
                      Mehr erfahren →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Halle (Saale) */}
          <div id="location-halle" className="scroll-mt-20">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden hover:shadow-3xl transition-shadow">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto order-2 md:order-1">
                  <img 
                    src="https://images.unsplash.com/photo-1543333995-a78aea2eee50?q=85&w=800" 
                    alt="Pflege in Halle" 
                    className="w-full h-full object-cover"
                    data-testid="halle-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-turquoise-400/20 to-transparent"></div>
                </div>
                <div className="p-8 md:p-12 order-1 md:order-2">
                  <div className="mb-4">
                    <span className="bg-turquoise-400 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      NEU ab 1.12.2025
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                    <span className="bg-turquoise-400 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4">H</span>
                    Halle (Saale)
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Adresse
                      </h4>
                      <p className="text-gray-600 ml-7">
                        Ambulante Pflege Großstadtherzen® Halle UG<br />
                        Merseburger Str. 19<br />
                        06112 Halle (Saale)
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Kontakt
                      </h4>
                      <p className="text-gray-600 ml-7">
                        <a href="tel:034568669465" className="hover:text-turquoise-400 transition-colors">Telefon: 0345-68669465</a><br />
                        <span className="text-gray-500">Fax: 0345-68669469</span><br />
                        <a href="mailto:info@grossstadtherzen-halle.de" className="hover:text-turquoise-400 transition-colors">info@grossstadtherzen-halle.de</a>
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-turquoise-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Bürozeiten ab 1.12.25
                      </h4>
                      <div className="text-gray-600 ml-7">
                        <p>Montag - Freitag: 8:00 - 16:00 Uhr</p>
                      </div>
                    </div>
                    
                    <Link 
                      to="/halle"
                      className="inline-block bg-turquoise-400 text-white px-6 py-3 rounded-full font-semibold hover:bg-turquoise-500 transition-colors mt-4"
                      data-testid="halle-details-link"
                    >
                      Mehr erfahren →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-white" data-testid="team-section">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_bdf34f10-1a0a-4946-884b-914a0658d72a/artifacts/2xrnpwh3_picture-1600.jpg" 
                alt="Unser Team" 
                className="rounded-3xl shadow-2xl"
                data-testid="team-image"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Unser Team</h2>
              <p className="text-lg text-gray-600 mb-6">
                Bei Großstadtherzen arbeiten erfahrene und qualifizierte Pflegefachkräfte, die ihre Arbeit mit Leidenschaft und Hingabe ausüben.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Wir legen großen Wert auf kontinuierliche Weiterbildung und persönliche Betreuung, damit Sie sich bei uns rundum wohl und gut aufgehoben fühlen.
              </p>
              <p className="text-lg text-gray-600">
                Ihre Zufriedenheit und Ihr Wohlbefinden stehen bei uns an erster Stelle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-b from-turquoise-400 to-turquoise-500 text-white" data-testid="contact-section">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Haben Sie Fragen?</h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto">
            Wir sind gerne für Sie da. Kontaktieren Sie uns telefonisch oder per E-Mail.
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all">
              <h3 className="text-2xl font-bold mb-4">Leipzig</h3>
              <p className="mb-2">
                <a href="tel:034124927833" className="hover:underline">0341-24927833</a>
              </p>
              <p>
                <a href="mailto:info@grossstadtherzen.de" className="hover:underline">info@grossstadtherzen.de</a>
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all">
              <h3 className="text-2xl font-bold mb-4">Halle (Saale)</h3>
              <p className="mb-2">
                <a href="tel:034568669465" className="hover:underline">0345-68669465</a>
              </p>
              <p>
                <a href="mailto:info@grossstadtherzen-halle.de" className="hover:underline">info@grossstadtherzen-halle.de</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;